import React, {useState} from 'react'
import {nanoid} from 'nanoid'
import {defineMessage, useIntl} from 'react-intl'
import {useCheckout} from '../../checkout/util/checkout-context'

import {
    useShopperCustomersMutation,
    useShopperBasketsMutation
} from '@salesforce/commerce-sdk-react'
import {Box} from '@salesforce/retail-react-app/app/components/shared/ui'
import {useCurrentCustomer} from '@salesforce/retail-react-app/app/hooks/use-current-customer'
import {useCurrentBasket} from '@salesforce/retail-react-app/app/hooks/use-current-basket'
import {ToggleCard, ToggleCardEdit, ToggleCardSummary} from '../../../components/toggle-card'
import ShippingAddressSelection from './shipping-address-selection'
import AddressDisplay from '../../../components/address-display'
import {pxToRem} from '../../../utills/pxToRem'
import PropTypes from 'prop-types'
import BopisAddress from './bopis-address'

const submitButtonMessage = defineMessage({
    defaultMessage: 'Save and Continue',
    id: 'shipping_address.button.continue_shipping'
})
const shippingAddressAriaLabel = defineMessage({
    defaultMessage: 'Shipping Address Form',
    id: 'shipping_address.label.shipping_address_form'
})

export default function ShippingAddress({isAllBopisItems}) {
    const {formatMessage} = useIntl()
    const [isLoading, setIsLoading] = useState()
    const {data: customer} = useCurrentCustomer()
    const {data: basket} = useCurrentBasket()
    const shipment = basket?.shipments.find((shipMnt) => !('c_shipmentType' in shipMnt))
    const shipmentId = shipment?.shipmentId
    const selectedShippingAddress = isAllBopisItems ? false : shipment?.shippingAddress
    const {step, STEPS, goToStep, goToNextStep} = useCheckout()
    const createCustomerAddress = useShopperCustomersMutation('createCustomerAddress')
    const updateCustomerAddress = useShopperCustomersMutation('updateCustomerAddress')
    const updateShippingAddressForShipment = useShopperBasketsMutation(
        'updateShippingAddressForShipment'
    )

    const submitAndContinue = async (address) => {
        setIsLoading(true)
        const {
            addressId,
            address1,
            city,
            countryCode,
            firstName,
            lastName,
            phone,
            postalCode,
            stateCode,
            c_addressName
        } = address
        await updateShippingAddressForShipment.mutateAsync({
            parameters: {
                basketId: basket.basketId,
                shipmentId: shipmentId || 'me',
                useAsBilling: false
            },
            body: {
                address1,
                city,
                countryCode: (countryCode || 'US').toUpperCase(),
                firstName,
                lastName,
                phone,
                postalCode,
                stateCode,
                c_addressName
            }
        })

        if (customer.isRegistered && !addressId) {
            const body = {
                address1,
                city,
                countryCode: (countryCode || 'US').toUpperCase(),
                firstName,
                lastName,
                phone,
                postalCode,
                stateCode,
                addressId: nanoid(),
                c_addressName
            }
            await createCustomerAddress.mutateAsync({
                body,
                parameters: {customerId: customer.customerId}
            })
        }

        if (customer.isRegistered && addressId) {
            await updateCustomerAddress.mutateAsync({
                body: {...address, countryCode: (countryCode || 'US').toUpperCase()},
                parameters: {
                    customerId: customer.customerId,
                    addressName: addressId
                }
            })
        }

        goToNextStep()
        setIsLoading(false)
    }

    return (
        <ToggleCard
            id="step-1"
            step={2}
            title={formatMessage({
                defaultMessage: 'Shipping',
                id: 'shipping_address.title.shipping'
            })}
            selectedShippingAddress={selectedShippingAddress}
            editing={step === STEPS.SHIPPING_ADDRESS}
            isLoading={isLoading}
            disabled={step === STEPS.CONTACT_INFO && !selectedShippingAddress}
            onEdit={() => goToStep(STEPS.SHIPPING_ADDRESS)}
            editLabel={formatMessage({
                defaultMessage: 'Edit',
                id: 'toggle_card.action.edit'
            })}
            disableEdit={isAllBopisItems}
        >
            <ToggleCardEdit>
                <ShippingAddressSelection
                    selectedAddress={selectedShippingAddress}
                    submitButtonLabel={submitButtonMessage}
                    onSubmit={submitAndContinue}
                    formTitleAriaLabel={shippingAddressAriaLabel}
                    isAllBopisItems={isAllBopisItems}
                />
            </ToggleCardEdit>
            <ToggleCardSummary>
                {isAllBopisItems ? (
                    <BopisAddress disableContinue={true} />
                ) : (
                    <>
                        {selectedShippingAddress && (
                            <>
                                <Box
                                    marginBottom={pxToRem(10)}
                                    fontSize={{base: pxToRem(14), lg: pxToRem(16)}}
                                    fontWeight={500}
                                    lineHeight={'normal'}
                                    letterSpacing={pxToRem(1)}
                                >
                                    {formatMessage({
                                        defaultMessage: 'Shipping Address',
                                        id: 'shipping_address.title.shipping_address'
                                    })}
                                </Box>
                                <AddressDisplay
                                    address={selectedShippingAddress}
                                    showAddressName={false}
                                />
                            </>
                        )}
                    </>
                )}
            </ToggleCardSummary>
        </ToggleCard>
    )
}

ShippingAddress.propTypes = {
    isAllBopisItems: PropTypes.bool
}
